import { useLocation, useParams } from 'react-router-dom';

import { BadgeCounter } from '@components/badge-counter';
import { IconForm } from '@components/icons/form';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconJob } from '@components/icons/job';
import { IconSettings } from '@components/icons/settings';
import { IconTeam } from '@components/icons/team';
import { Nav } from '@components/nav';
import { HireNav } from '@components/nav/nav';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';

import { useGetJob } from 'api/jobs/hooks';
import { Loading } from 'components/Loading';
import {
  getMissingApplicationFormTranslations,
  getMissingJobPostingTranslations,
} from 'pages/CreateJobNew/helpers';
import { JobLanguage } from 'types/jobs';

export type LocationState = {
  from: string;
};

export const CreateJobNavigation = () => {
  const { t } = useScopedTranslate(navLocale);
  const location = useLocation();
  const params = useParams();
  const { from } = (location.state as LocationState) || { from: '/' };

  const isEdit = location.pathname.includes('edit');

  const jobId = params['*']?.split('/')[0];

  const jobQuery = useGetJob(Number(jobId), isEdit ? jobId !== undefined : false);

  if (jobQuery.isLoading && jobQuery.fetchStatus !== 'idle') {
    return (
      <HireNav
        variant="static"
        fixedHeader={<Nav.Header logo={<Nav.HeaderBackLink href={from} label={t('back')} />} />}
      >
        <Loading />
      </HireNav>
    );
  }

  let accessCount = undefined;
  let missingTranslationJobPosting = false;
  let missingTranslationApplicationForm = false;

  if (jobQuery.data) {
    const job = jobQuery.data;
    const languages: CareerSiteLanguageType[] = job.languages.map((l: JobLanguage) => l.language);
    accessCount = job.numActiveFollowers;

    if (languages.length > 1) {
      missingTranslationJobPosting = Object.values(
        getMissingJobPostingTranslations(job, languages),
      ).some((value) => value === true);
      missingTranslationApplicationForm = Object.values(
        getMissingApplicationFormTranslations(job, languages),
      ).some((value) => value === true);
    }
  }

  return (
    <HireNav
      variant="static"
      fixedHeader={<Nav.Header logo={<Nav.HeaderBackLink href={from} label={t('back')} />} />}
    >
      <Nav.Group>
        {!isEdit ? (
          <Nav.Link label={t('jobPosting')} iconLeft={<IconJob size={16} />} href="/job/create" />
        ) : (
          <Nav.Link
            label={t('jobPosting')}
            iconLeft={<IconJob size={16} />}
            href={`/job/${jobId}/edit/description`}
            iconRight={
              missingTranslationJobPosting ? (
                <IconFormIndicatorError color="error" size={16} />
              ) : undefined
            }
          />
        )}
        <Nav.Link
          label={t('applicationForm')}
          iconLeft={<IconForm size={16} />}
          href={`/job/${jobId}/edit/form`}
          isDisabled={!isEdit}
          iconRight={
            missingTranslationApplicationForm ? (
              <IconFormIndicatorError color="error" size={16} />
            ) : undefined
          }
        />
      </Nav.Group>
      <Nav.Separator />
      <Nav.Group>
        <Nav.Link
          label={t('access')}
          iconLeft={<IconTeam size={16} />}
          href={`/job/${jobId}/edit/access`}
          isDisabled={!isEdit}
          iconRight={accessCount ? <BadgeCounter value={accessCount} size="sm" /> : undefined}
        />
        <Nav.Link
          label={t('settings')}
          iconLeft={<IconSettings size={16} />}
          href={`/job/${jobId}/edit/settings`}
          isDisabled={!isEdit}
        />
      </Nav.Group>
    </HireNav>
  );
};

const navLocale = {
  en: {
    back: 'Back',
    jobPosting: 'Job posting',
    applicationForm: 'Application form',
    access: 'Access',
    settings: 'Settings',
  },
  is: {
    back: 'Til baka',
    jobPosting: 'Starfslýsing',
    applicationForm: 'Umsóknarform',
    access: 'Aðgangur',
    settings: 'Stillingar',
  },
};
